
import React from 'react';
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ConnectionStatus from '@/components/ConnectionStatus';
import DoctorList from '@/components/doctors/DoctorList';
import DoctorFormDialog from '@/components/doctors/DoctorFormDialog';
import DeleteConfirmationDialog from '@/components/doctors/DeleteConfirmationDialog';
import ScheduleDialog from '@/components/doctors/ScheduleDialog';
import DoctorActionButtons from '@/components/doctors/DoctorActionButtons';
import { useDoctorManagement } from '@/hooks/useDoctorManagement';
import { PlusCircle, RefreshCw } from 'lucide-react';

const DoctorManagement: React.FC = () => {
  const {
    doctors,
    isLoading,
    connectionError,
    currentDoctor,
    newDoctor,
    imageFile,
    imagePreview,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isScheduleDialogOpen,
    handleFileChange,
    handleImageUrlChange,
    handleInputChange,
    handleAddDoctor,
    handleEditDoctor,
    handleUpdateDoctor,
    handleDeletePrompt,
    handleDeleteDoctor,
    handleScheduleManagement,
    handleCreateSampleRecords,
    loadDoctors,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsScheduleDialogOpen,
    resetForm
  } = useDoctorManagement();

  // Force refresh function
  const handleForceRefresh = () => {
    console.log("Force refreshing doctor data...");
    loadDoctors();
  };

  return (
    <div className="container mx-auto space-y-6">
      {/* Connection status component to show when there's a connection issue */}
      {connectionError && <ConnectionStatus />}
      
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Doctor Management</h1>
          <p className="text-sm text-muted-foreground">
            {isLoading ? 'Loading doctors...' : doctors.length === 0 ? 
            'No doctors found' : `Showing ${doctors.length} doctors`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleForceRefresh}
            disabled={isLoading}
            title="Refresh doctors list"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={isEditDialogOpen && !currentDoctor} onOpenChange={(open) => {
            if (open) {
              resetForm();
            }
            setIsEditDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                Add Doctor
              </Button>
            </DialogTrigger>
          </Dialog>
          <DoctorActionButtons 
            onCreateSampleRecords={handleCreateSampleRecords} 
          />
        </div>
      </div>
      
      <DoctorList 
        doctors={doctors}
        isLoading={isLoading}
        onEditDoctor={handleEditDoctor}
        onDeleteDoctor={handleDeletePrompt}
        onManageSchedule={handleScheduleManagement}
        onRetryConnection={loadDoctors}
        hasConnectionError={connectionError}
      />
      
      {/* Add Doctor Dialog - Shown when isEditDialogOpen is true and currentDoctor is null */}
      <DoctorFormDialog
        isOpen={isEditDialogOpen && !currentDoctor}
        doctor={newDoctor}
        isEditing={false}
        onSave={handleAddDoctor}
        onInputChange={handleInputChange}
        onCancel={() => setIsEditDialogOpen(false)}
        imageFile={imageFile}
        imagePreview={imagePreview}
        onFileChange={handleFileChange}
        onImageUrlChange={handleImageUrlChange}
      />
      
      {/* Edit Doctor Dialog - Shown when isEditDialogOpen is true and currentDoctor exists */}
      <DoctorFormDialog
        isOpen={isEditDialogOpen && !!currentDoctor}
        doctor={newDoctor}
        isEditing={true}
        onSave={handleUpdateDoctor}
        onInputChange={handleInputChange}
        onCancel={() => {
          resetForm();
          setIsEditDialogOpen(false);
        }}
        imageFile={imageFile}
        imagePreview={imagePreview}
        onFileChange={handleFileChange}
        onImageUrlChange={handleImageUrlChange}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        doctor={currentDoctor}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteDoctor}
      />
      
      {/* Schedule Management Dialog */}
      <ScheduleDialog
        isOpen={isScheduleDialogOpen}
        doctor={currentDoctor}
        onClose={() => setIsScheduleDialogOpen(false)}
      />
    </div>
  );
};

export default DoctorManagement;
